// Configurations
import APIConfig from '@/config/api'

// Types
import type { APIUserCheckOnlineResult } from '@/types/api/(site)/user/check-online'
import type { ProbeEndpointProps } from '@/types/helpers/api/probe-endpoint'

const isReachablePayload = (data: APIUserCheckOnlineResult | null): boolean => {
	if (!data) return false

	return !!(data.status && (data.statusCode === 200 || data.statusCode === 204))
}

const probeWithCors = async (url: string, signal?: AbortSignal): Promise<boolean | null> => {
	try {
		const res = await fetch(url, {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'X-API-Version': APIConfig.version
			},
			signal,
			cache: 'no-store'
		})

		if (!res.ok) return false

		const data: APIUserCheckOnlineResult = await res.json().catch(() => null)

		if (!data) return true

		return isReachablePayload(data)
	} catch {
		if (signal?.aborted) return false

		// CORS / network failure - caller may fall back to no-cors reachability
		return null
	}
}

const probeWithNoCors = async (url: string, signal?: AbortSignal): Promise<boolean> => {
	try {
		await fetch(url, {
			method: 'GET',
			mode: 'no-cors',
			signal,
			cache: 'no-store'
		})

		// Opaque response means the host answered; connection refused / DNS fail throws.
		return true
	} catch {
		return false
	}
}

const probeAPIEndpoint = async (props: ProbeEndpointProps): Promise<boolean> => {
	// Props
	const { apiUrl, signal } = props

	if (!apiUrl) return false

	const rootResult = await probeWithCors(apiUrl, signal)

	if (rootResult === true) return true
	if (signal?.aborted) return false

	const checkOnlineResult = await probeWithCors(`${apiUrl}/check-online`, signal)

	if (checkOnlineResult === true) return true
	if (signal?.aborted) return false

	// Browser may block cross-origin reads (e.g. cloud Laravel). Still detect reachability.
	if (rootResult === null || checkOnlineResult === null) {
		return probeWithNoCors(apiUrl, signal)
	}

	return false
}

export default probeAPIEndpoint
